/* eslint-disable max-len */
import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
} from '@angular/core';

import moment from 'moment-timezone';
import { DomHandler } from 'primeng/dom';

import { ActionKeys } from '../../../../Common/Forms/enums/keys.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { DatePickerService } from '../../services/date-picker.service';
import { CalendarViewsEnum, NavigationDirectionsEnum } from '../../enums/date-picker.enum';
import { DateHelper } from '../../helpers/date.helper';

/**
 * The calendar itself: header (prev / month / year / next), and the date, month and year grids.
 *
 * It owns navigation and selection state but knows nothing about inputs, overlays or positioning,
 * so it can be dropped into any host (popup, dropdown, inline).
 *
 * Tab-trapping and Escape are handled by the surrounding `agpr-date-picker-overlay`.
 *
 * NOTE: `DatePickerService` is not provided here; it is resolved from the host component's providers.
 *
 * The calendar works in local calendar days. So whatever you pass in gets read as “what day is this for this user?”
 */
@Component({
    selector: 'agpr-date-picker-panel',
    templateUrl: './date-picker-panel.component.html',
    styleUrls: ['./date-picker-panel.component.scss'],
    standalone: false
})
export class DatePickerPanelComponent implements OnInit, OnChanges {

    @Input() value: any;
    @Input() selectionMode: string = 'single';
    @Input() view: CalendarViewsEnum = CalendarViewsEnum.DATE;
    @Input() defaultDate: Date;
    @Input() minDate: Date;
    @Input() maxDate: Date;

    /** Only these days are selectable. null/undefined = no restriction. Prefer 'YYYY-MM-DD' strings. */
    @Input() availableDates: (Date | string)[] | null;

    @Input() disabledDates: Date[];
    @Input() disabledDays: number[];
    @Input() disabled: boolean;
    @Input() dateTemplate: TemplateRef<any>;

    /** Emits the new model value (Date, or [start, end] in range mode). */
    @Output() valueChange = new EventEmitter<any>();
    /** Emits the date that was clicked / chosen, after `valueChange`. */
    @Output() dateSelect = new EventEmitter<Date>();
    @Output() monthChange = new EventEmitter<{ month: number; year: number }>();
    @Output() yearChange = new EventEmitter<{ month: number; year: number }>();

    month: any;
    weekDays: string[] = [];
    currentView: CalendarViewsEnum;
    currentMonth: number;
    currentYear: number;
    monthTitleValues: string[] = [];
    monthPickerValues: string[] = [];
    yearPickerValues: string[] = [];

    prevButtonLabel: string;
    prevButtonAriaLabel: string;
    nextButtonLabel: string;
    nextButtonAriaLabel: string;
    monthButtonAriaLabel: string;
    yearButtonAriaLabel: string;

    readonly calendarViews = CalendarViewsEnum;

    private initialized = false;
    private availableDateKeys: Set<string> | null = null;

    constructor(
        private el: ElementRef<HTMLElement>,
        private localizationService: LocalizationService,
        private datePickerService: DatePickerService
    ) {}

    ngOnInit(): void {
        const date = this.defaultDate || new Date();
        this.currentMonth = date.getMonth();
        this.currentYear = date.getFullYear();
        this.currentView = this.view;

        this.weekDays = this.datePickerService.getWeekDays();
        this.monthTitleValues = this.datePickerService.getMonthTitleValues();
        this.monthPickerValues = this.datePickerService.getMonthPickerValues();
        this.yearPickerValues = this.datePickerService.getYearPickerValues(this.currentYear);

        this.initialized = true;
        this.render();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['availableDates']) {
            this.availableDateKeys = this.buildAvailableDateKeys();
        }

        if (!this.initialized) {
            return;
        }
        if (changes['view']) {
            this.currentView = this.view;
        }
        if (changes['defaultDate']) {
            const date = this.defaultDate || new Date();
            this.currentMonth = date.getMonth();
            this.currentYear = date.getFullYear();
            this.yearPickerValues = this.datePickerService.getYearPickerValues(this.currentYear);
        }
        // value / minDate / maxDate / disabledDates / disabledDays / selectionMode all affect the grid
        this.render();
    }


    /** Rebuilds the grid and labels without touching focus. */
    render(): void {
        this.month = this.datePickerService.createMonth(
            this.value, this.currentMonth, this.currentYear, this.minDate, this.maxDate, this.disabledDates, this.disabledDays, this.selectionMode
        );
        this.applyAvailableDates();
        this.updateAccessibleLabels();
    }

    /** Rebuilds the grid and lets the service set up roving tabindex / focus. */
    updatePanelView(focusDirection: NavigationDirectionsEnum = null): void {
        this.render();
        requestAnimationFrame(() => {
            this.datePickerService.initFocus(this.el.nativeElement, this.currentView, focusDirection);
        });
    }

    /**
     * Moves the calendar to the month containing `value` (first date for ranges).
     * Falls back to `defaultDate`, then today.
     */
    navigateTo(value: Date | Date[] | null): void {
        const candidate = Array.isArray(value) ? value[0] : value;
        const date: Date = !candidate && this.defaultDate && moment(this.defaultDate).isValid() ?
            this.defaultDate :
            candidate && moment(candidate).isValid() ? candidate : new Date();
        this.currentMonth = date.getMonth();
        this.currentYear = date.getFullYear();
        this.updatePanelView();
    }

    /** Returns to the configured starting view (call when the popup closes). */
    resetView(): void {
        this.currentView = this.view;
        this.render();
    }

    /** Focuses the active date cell, or the "previous" button when there is none. */
    focusInitial(): void {
        requestAnimationFrame(() => {
            const host = this.el.nativeElement;
            const cell = host.querySelector<HTMLElement>('.agpr-datePicker-dateCell[tabindex="0"]');
            (cell ?? host.querySelector<HTMLElement>('.agpr-datePicker-panel-prevButton'))?.focus();
        });
    }


    switchToMonthView(): void {
        this.currentView = CalendarViewsEnum.MONTH;
        this.updatePanelView();
    }

    switchToYearView(): void {
        this.currentView = CalendarViewsEnum.YEAR;
        // Recompute so the decade matches the year we navigated to.
        this.yearPickerValues = this.datePickerService.getYearPickerValues(this.currentYear);
        this.updatePanelView();
    }

    onPrevButtonClick(): void {
        this.navBackward();
        setTimeout(() => this.el.nativeElement.querySelector<HTMLElement>('.agpr-datePicker-panel-prevButton')?.focus(), 100);
    }

    onNextButtonClick(): void {
        this.navForward();
        setTimeout(() => this.el.nativeElement.querySelector<HTMLElement>('.agpr-datePicker-panel-nextButton')?.focus(), 100);
    }

    navigateToMonth(prev: boolean): void {
        if (prev) {
            this.navBackward(NavigationDirectionsEnum.PREV);
        } else {
            this.navForward(NavigationDirectionsEnum.NEXT);
        }
    }

    navBackward(focusDirection: NavigationDirectionsEnum = null): void {
        switch (this.currentView) {
            case CalendarViewsEnum.DATE:
                if (this.currentMonth === 0) {
                    this.currentMonth = 11;
                    this.currentYear--;
                } else {
                    this.currentMonth--;
                }
                this.monthChange.emit({ month: this.currentMonth + 1, year: this.currentYear });
                break;
            case CalendarViewsEnum.MONTH:
                this.currentYear--;
                break;
            case CalendarViewsEnum.YEAR:
                this.currentYear = this.currentYear - 10;
                this.yearPickerValues = this.datePickerService.getYearPickerValues(this.currentYear);
                break;
        }
        this.updatePanelView(focusDirection);
    }

    navForward(focusDirection: NavigationDirectionsEnum = null): void {
        switch (this.currentView) {
            case CalendarViewsEnum.DATE:
                if (this.currentMonth === 11) {
                    this.currentMonth = 0;
                    this.currentYear++;
                } else {
                    this.currentMonth++;
                }
                this.monthChange.emit({ month: this.currentMonth + 1, year: this.currentYear });
                break;
            case CalendarViewsEnum.MONTH:
                this.currentYear++;
                break;
            case CalendarViewsEnum.YEAR:
                this.currentYear = this.currentYear + 10;
                this.yearPickerValues = this.datePickerService.getYearPickerValues(this.currentYear);
                break;
        }
        this.updatePanelView(focusDirection);
    }

    onDateSelect(event: Event, dateMeta: any): void {
        if (this.disabled || !dateMeta.selectable) {
            return;
        }
        const date = this.selectDate(dateMeta);
        this.updatePanelView();
        this.dateSelect.emit(date);
    }

    onMonthSelect(event: Event, index: number): void {
        if (this.view === CalendarViewsEnum.MONTH) {
            this.onDateSelect(event, { year: this.currentYear, month: index, day: 1, selectable: true });
        } else {
            this.currentMonth = index;
            this.currentView = CalendarViewsEnum.DATE;
            this.updatePanelView(NavigationDirectionsEnum.NEXT);
            this.monthChange.emit({ month: this.currentMonth + 1, year: this.currentYear });
        }
    }

    onYearSelect(event: Event, year: any): void {
        if (this.view === CalendarViewsEnum.YEAR) {
            this.onDateSelect(event, { year: year, month: 0, day: 1, selectable: true });
        } else {
            this.currentYear = +year;
            this.currentView = CalendarViewsEnum.MONTH;
            this.updatePanelView(NavigationDirectionsEnum.NEXT);
            this.yearChange.emit({ month: this.currentMonth + 1, year: this.currentYear });
        }
    }

    private selectDate(dateMeta: any): Date {
        const date = new Date(dateMeta.year, dateMeta.month, dateMeta.day);

        if (this.minDate && this.minDate > date) {
            date.setTime(+this.minDate);
        }
        if (this.maxDate && this.maxDate < date) {
            date.setTime(+this.maxDate);
        }

        let newValue: any = this.value;
        if (this.selectionMode === 'single') {
            newValue = date;
        } else if (this.selectionMode === 'range') {
            if (this.value?.length) {
                let startDate = this.value[0];
                let endDate = this.value[1];

                if (!endDate && date.getTime() >= startDate.getTime()) {
                    endDate = date;
                } else {
                    startDate = date;
                    endDate = null;
                }
                newValue = [startDate, endDate];
            } else {
                newValue = [date, null];
            }
        }

        // Keep local state in sync so the panel also works when the host doesn't bind [value].
        this.value = newValue;
        this.valueChange.emit(newValue);
        return date;
    }

    onDateCellKeydown(event: KeyboardEvent, date: any): void {
        const cell = event.currentTarget as HTMLElement;
        let cellIndex: number;

        switch (event.key) {
            case ActionKeys.DOWN: {
                cell.setAttribute('tabIndex', '-1');
                cellIndex = DomHandler.index(cell);
                const nextRow = cell.parentElement.nextElementSibling;
                if (nextRow) {
                    const focusCell = nextRow.children[cellIndex] as HTMLElement;
                    if (DomHandler.hasClass(focusCell, 'agpr-datePicker-dateCell--disabled')) {
                        this.navForward(NavigationDirectionsEnum.NEXT);
                    } else {
                        focusCell.setAttribute('tabIndex', '0');
                        focusCell.focus();
                    }
                } else {
                    this.navForward(NavigationDirectionsEnum.NEXT);
                }
                event.preventDefault();
                break;
            }
            case ActionKeys.UP: {
                cell.setAttribute('tabIndex', '-1');
                cellIndex = DomHandler.index(cell);
                const prevRow = cell.parentElement.previousElementSibling;
                if (prevRow) {
                    const focusCell = prevRow.children[cellIndex] as HTMLElement;
                    if (DomHandler.hasClass(focusCell, 'agpr-datePicker-dateCell--disabled')) {
                        this.navBackward(NavigationDirectionsEnum.PREV);
                    } else {
                        focusCell.setAttribute('tabIndex', '0');
                        focusCell.focus();
                    }
                } else {
                    this.navBackward(NavigationDirectionsEnum.PREV);
                }
                event.preventDefault();
                break;
            }
            case ActionKeys.LEFT: {
                cell.setAttribute('tabIndex', '-1');
                const prevCell = cell.previousElementSibling as HTMLElement;
                if (prevCell && !DomHandler.hasClass(prevCell, 'agpr-datePicker-dateCell--disabled')) {
                    prevCell.setAttribute('tabIndex', '0');
                    prevCell.focus();
                } else {
                    this.navigateToMonth(true);
                }
                event.preventDefault();
                break;
            }
            case ActionKeys.RIGHT: {
                cell.setAttribute('tabIndex', '-1');
                const nextCell = cell.nextElementSibling as HTMLElement;
                if (nextCell && !DomHandler.hasClass(nextCell, 'agpr-datePicker-dateCell--disabled')) {
                    nextCell.setAttribute('tabIndex', '0');
                    nextCell.focus();
                } else {
                    this.navigateToMonth(false);
                }
                event.preventDefault();
                break;
            }
            case ActionKeys.ENTER:
            case ActionKeys.SPACE:
                this.onDateSelect(event, date);
                break;
        }
    }

    onListCellKeydown(event: KeyboardEvent, index: any): void {
        const cell = event.currentTarget as HTMLElement;

        switch (event.key) {
            case ActionKeys.DOWN:
            case ActionKeys.UP:
            case ActionKeys.LEFT:
            case ActionKeys.RIGHT: {
                const step = { [ActionKeys.DOWN]: 7, [ActionKeys.UP]: -7, [ActionKeys.LEFT]: -1, [ActionKeys.RIGHT]: 1 }[event.key];
                cell.setAttribute('tabIndex', '-1');
                const target = this.findSelectableCell(cell, step);
                if (target) {
                    target.setAttribute('tabIndex', '0');
                    target.focus();
                } else if (step > 0) {
                    this.navForward(NavigationDirectionsEnum.NEXT);
                } else {
                    this.navBackward(NavigationDirectionsEnum.PREV);
                }
                event.preventDefault();
                break;
            }
            case ActionKeys.ENTER:
            case ActionKeys.SPACE: {
                if (this.currentView === CalendarViewsEnum.MONTH) {
                    this.onMonthSelect(event, index);
                } else {
                    this.onYearSelect(event, index);
                }
                break;
            }
        }
    }


    private buildAvailableDateKeys(): Set<string> | null {
        if (!this.availableDates) {
            return null;
        }
        return new Set(this.availableDates.map(value => {
            if (value instanceof Date) {
                return DateHelper.toDayKey(value);
            }

            const [y, m, d] = value.slice(0, 10).split('-').map(Number);
            return this.toDateKey(y, m - 1, d);
        }));
    }

    /** Marks every day that isn't in availableDates as unselectable. */
    private applyAvailableDates(): void {
        if (!this.availableDateKeys || !this.month) {
            return;
        }
        for (const week of this.month.dates) {
            for (const date of week) {
                if (date.selectable && !this.availableDateKeys.has(this.toDateKey(date.year, date.month, date.day))) {
                    date.selectable = false;
                }
            }
        }
    }

    private toDateKey(year: number, month: number, day: number): string {
        return `${year}-${month}-${day}`;
    }


    private updateAccessibleLabels(): void {
        const prevMonthAndYear = this.datePickerService.getPreviousMonthAndYear(this.currentMonth, this.currentYear);
        const nextMonthAndYear = this.datePickerService.getNextMonthAndYear(this.currentMonth, this.currentYear);

        switch (this.currentView) {
            case CalendarViewsEnum.DATE:
                this.prevButtonLabel = this.localizationService.transform('Common.datePicker.prevMonth');
                this.prevButtonAriaLabel = this.prevButtonLabel + ' ' + this.monthTitleValues[prevMonthAndYear.month];
                this.nextButtonLabel = this.localizationService.transform('Common.datePicker.nextMonth');
                this.nextButtonAriaLabel = this.nextButtonLabel + ' ' + this.monthTitleValues[nextMonthAndYear.month];
                this.monthButtonAriaLabel = this.monthTitleValues[this.currentMonth] + ', ' + this.localizationService.transform('Common.datePicker.changeMonth');
                this.yearButtonAriaLabel = this.currentYear + ', ' + this.localizationService.transform('Common.datePicker.changeYear');
                break;
            case CalendarViewsEnum.MONTH:
                this.prevButtonLabel = this.localizationService.transform('Common.datePicker.prevYear');
                this.prevButtonAriaLabel = this.prevButtonLabel + ' ' + (this.currentYear - 1);
                this.nextButtonLabel = this.localizationService.transform('Common.datePicker.nextYear');
                this.nextButtonAriaLabel = this.nextButtonLabel + ' ' + (this.currentYear + 1);
                this.yearButtonAriaLabel = this.currentYear + ', ' + this.localizationService.transform('Common.datePicker.changeYear');
                break;
            case CalendarViewsEnum.YEAR: {
                this.prevButtonLabel = this.localizationService.transform('Common.datePicker.prevDecade');
                const prevDecade = this.datePickerService.getYearPickerValues(this.currentYear - 10);
                this.prevButtonAriaLabel = this.prevButtonLabel + ', ' + prevDecade[0] + ' to ' + prevDecade[prevDecade.length - 1];
                this.nextButtonLabel = this.localizationService.transform('Common.datePicker.nextDecade');
                const nextDecade = this.datePickerService.getYearPickerValues(this.currentYear + 10);
                this.nextButtonAriaLabel = this.nextButtonLabel + ', ' + nextDecade[0] + ' to ' + nextDecade[nextDecade.length - 1];
                break;
            }
        }
    }

    /** Walks the grid in `step` increments (±1 = horizontal, ±7 = vertical) to the next selectable day. */
    private findSelectableCell(from: HTMLElement, step: number): HTMLElement | null {
        const cells = Array.from(from.closest('tbody').querySelectorAll<HTMLElement>('.agpr-datePicker-dateCell'));
        for (let i = cells.indexOf(from) + step; i >= 0 && i < cells.length; i += step) {
            if (!cells[i].classList.contains('agpr-datePicker-dateCell--disabled')) {
                return cells[i];
            }
        }
        return null;
    }
}
